// ============================================================================
// frontend/src/components/tenant/ProvisioningStatus.tsx
// ============================================================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ProvisioningSteps } from '@/types/tenant';

interface ProvisioningStatusProps {
  status: string | null;
  steps: ProvisioningSteps;
  elapsedTime: number;
  deployedUrl: string;
}

export const ProvisioningStatus: React.FC<ProvisioningStatusProps> = ({
  status,
  steps,
  elapsedTime,
  deployedUrl
}) => {
  const navigate = useNavigate();

  if (!status) return null;

  return (
    <div className={`mb-6 rounded-lg border transition-all duration-500 ${
      status === 'deployed' 
        ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/50 dark:to-emerald-950/50 shadow-lg shadow-green-500/20' 
        : status === 'error'
        ? 'border-red-500 bg-red-50 dark:bg-red-950/50'
        : 'border-blue-500 bg-blue-50 dark:bg-blue-950/50'
    } p-6`}>
      <div className="flex items-center gap-4 mb-6">
        {status === 'deployed' ? (
          <div className="relative">
            <svg className="w-16 h-16 text-green-600 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="absolute inset-0 bg-green-400 rounded-full blur-xl opacity-30 animate-pulse"></div>
          </div>
        ) : status === 'error' ? (
          <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ) : (
          <div className="relative">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
        )}
        <div className="flex-1">
          <h3 className={`text-2xl font-bold mb-1 ${
            status === 'deployed' ? 'text-green-900 dark:text-green-100' :
            status === 'error' ? 'text-red-900 dark:text-red-100' :
            'text-blue-900 dark:text-blue-100'
          }`}>
            {status === 'creating_tenant' && '📝 Création du tenant...'}
            {status === 'provisioning' && '🚀 Déploiement en cours...'}
            {status === 'deployed' && '🎉 Déploiement réussi !'}
            {status === 'error' && '❌ Erreur lors du déploiement'}
          </h3>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {status === 'deployed' 
              ? `Votre tenant est maintenant accessible !`
              : status === 'error'
              ? 'Une erreur est survenue pendant le provisioning'
              : `Cette opération peut prendre 5-15 minutes (${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, '0')} écoulées)`
            }
          </p>
        </div>
      </div>

      {/* Steps */}
      {Object.keys(steps).length > 0 && (
        <div className="space-y-3">
          {Object.entries(steps).map(([step, stepStatus], index) => {
            const isCompleted = ['completed', 'created', 'deployed', 'configured', 'granted', 'initialized', 'active', 'ready', 'sent'].includes(stepStatus);
            const isError = ['error', 'failed'].includes(stepStatus);
            
            return (
              <div 
                key={step} 
                className={`flex items-center gap-4 p-4 rounded-lg transition-all duration-300 ${
                  isCompleted ? 'bg-green-100 dark:bg-green-900/30' :
                  isError ? 'bg-red-100 dark:bg-red-900/30' :
                  'bg-white dark:bg-gray-800'
                }`}
                style={{ animation: `slideIn 0.3s ease-out ${index * 0.1}s both` }}
              >
                {isCompleted ? (
                  <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : isError ? (
                  <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-blue-200 border-t-blue-600 flex-shrink-0"></div>
                )}
                <div className="flex-1">
                  <span className={`font-medium ${
                    isCompleted ? 'text-green-900 dark:text-green-100' :
                    isError ? 'text-red-900 dark:text-red-100' :
                    'text-blue-900 dark:text-blue-100'
                  }`}>
                    {step === 'tenant_db' && '💾 Tenant en base de données'}
                    {step === 'provisioning' && '⚙️ Lancement du provisioning'}
                    {step === 'database' && '📦 Création de la base de données'}
                    {step === 'schema' && '📋 Initialisation du schéma'}
                    {step === 'admin_user' && '👤 Création de l\'utilisateur admin'}
                    {step === 'static_ip' && '🌐 Configuration de l\'IP statique'}
                    {step === 'dns' && '🔗 Configuration du DNS'}
                    {step === 'gcs_access' && '☁️ Accès au stockage GCS'} 
                    {step === 'helm' && '⚙️ Déploiement Kubernetes'}
                    {step === 'branding_assets' && '🎨 Assets de branding'}
                    {step === 'certificate' && '🔒 Provisioning du certificat SSL'}
                    {step === 'welcome_email' && '📧 Envoi de l\'email de bienvenue'}
                    {step === 'managers_notification' && '📧 Notification aux gérants'}
                    {step === 'tenant_agents' && '🤖 Configuration des agents'}
                  </span>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {stepStatus === 'sent' && 'Email envoyé avec succès'}
                    {stepStatus === 'failed' && 'Échec de l\'envoi'}
                    {stepStatus}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Success actions */}
      {status === 'deployed' && deployedUrl && (
        <div className="mt-6 p-6 bg-white dark:bg-gray-800 rounded-lg border-2 border-green-500 space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            <div className="flex-1">
              <h4 className="font-semibold text-lg">Votre tenant est prêt !</h4>
              <p className="text-sm text-muted-foreground">Vous pouvez maintenant y accéder</p>
            </div>
          </div>
          
          <div className="flex gap-3 flex-wrap">
            <a
              href={deployedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-green-600 text-white font-medium hover:bg-green-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Ouvrir le tenant
            </a>
            <button
              onClick={() => navigate('/admin')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-input bg-background hover:bg-accent transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Retour au dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};